import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

/**
 * One instrument picked off a drawing, keyed by asset id (or normalized tag
 * text) so the same instrument selected from different boxes or pages stays
 * one entry.
 */
export interface SelectedInstrument {
  key: string
  label: string
  sourceLabel: string
  datapointIds: string[]
  assetId?: string
  subjectUid?: string
  documentId?: string
  boxIds: string[]
}

interface InstrumentSelectionContextValue {
  selected: Map<string, SelectedInstrument>
  select: (instrument: SelectedInstrument) => void
  toggle: (instrument: SelectedInstrument) => void
  addMany: (instruments: SelectedInstrument[]) => void
  remove: (key: string) => void
  clear: () => void
}

/**
 * Optional viewer capability: when a surface provides this context, box clicks
 * and marquee selections inside the document viewer switch from their default
 * semantics to instrument selection. Absent, the viewer is unchanged.
 */
const InstrumentSelectionContext =
  createContext<InstrumentSelectionContextValue | null>(null)

export const InstrumentSelectionProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const [selected, setSelected] = useState<Map<string, SelectedInstrument>>(
    () => new Map()
  )

  const select = useCallback((instrument: SelectedInstrument) => {
    setSelected(new Map([[instrument.key, instrument]]))
  }, [])

  const toggle = useCallback((instrument: SelectedInstrument) => {
    setSelected((prev) => {
      const next = new Map(prev)
      if (next.has(instrument.key)) {
        next.delete(instrument.key)
      } else {
        next.set(instrument.key, instrument)
      }
      return next
    })
  }, [])

  const addMany = useCallback((instruments: SelectedInstrument[]) => {
    if (instruments.length === 0) return
    setSelected((prev) => {
      const next = new Map(prev)
      for (const instrument of instruments) {
        const existing = next.get(instrument.key)
        next.set(
          instrument.key,
          existing
            ? {
                ...existing,
                boxIds: [
                  ...new Set([...existing.boxIds, ...instrument.boxIds]),
                ],
              }
            : instrument
        )
      }
      return next
    })
  }, [])

  const remove = useCallback((key: string) => {
    setSelected((prev) => {
      if (!prev.has(key)) return prev
      const next = new Map(prev)
      next.delete(key)
      return next
    })
  }, [])

  const clear = useCallback(() => {
    setSelected((prev) => (prev.size === 0 ? prev : new Map()))
  }, [])

  const value = useMemo(
    () => ({ selected, select, toggle, addMany, remove, clear }),
    [selected, select, toggle, addMany, remove, clear]
  )

  return (
    <InstrumentSelectionContext.Provider value={value}>
      {children}
    </InstrumentSelectionContext.Provider>
  )
}

export const useInstrumentSelectionOptional =
  (): InstrumentSelectionContextValue | null =>
    useContext(InstrumentSelectionContext)

export const useInstrumentSelection = (): InstrumentSelectionContextValue => {
  const context = useContext(InstrumentSelectionContext)
  if (!context) {
    throw new Error(
      'useInstrumentSelection must be used within an InstrumentSelectionProvider'
    )
  }
  return context
}
